// Predictive Search -- the <predictive-search> custom element that
// wraps the search popup's form + results container. Ported from
// orion-v3.0 and rewritten from jQuery to vanilla JS. Behaviour
// matches the original:
//
//   tier_1   No live fetching. Toggles the "Press Enter" hint
//            on / off based on whether the input is empty.
//   tier_2   Debounced fetch on input; results container fades
//            in / out (no Discovery initial state to swap with).
//   tier_3   Debounced fetch on input; on first results the
//            Discovery initial state fades out and the results
//            container fades in. Clearing input restores the
//            Discovery state.
//
// The no-results case adds `.search-results--no-results` to the
// section wrapper so the CSS can adjust layout.

const FADE_MS = 250;

function fadeIn(el) {
  if (!el) return;
  el.hidden = false;
  el.style.display = '';
  el.style.opacity = '0';
  requestAnimationFrame(() => {
    el.style.transition = `opacity ${FADE_MS}ms ease`;
    el.style.opacity = '1';
  });
}

function fadeOut(el, after) {
  if (!el) {
    if (after) after();
    return;
  }
  el.style.transition = `opacity ${FADE_MS}ms ease`;
  el.style.opacity = '0';
  window.setTimeout(() => {
    el.hidden = true;
    if (after) after();
  }, FADE_MS);
}

function isVisible(el) {
  if (!el) return false;
  if (el.hidden) return false;
  const cs = window.getComputedStyle(el);
  return cs.display !== 'none' && cs.visibility !== 'hidden';
}

function debounce(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('#search-input');
    this.predictiveSearchResults = document.querySelector('#search-results');
    this.liveRegion = this.querySelector('[data-search-live-region]');
    this.searchType = this.getAttribute('data-search-type');

    if (!this.input) return;

    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.input.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  // -- Keyboard navigation -------------------------------------------------

  getResultItems() {
    if (!this.predictiveSearchResults) return [];
    return Array.from(
      this.predictiveSearchResults.querySelectorAll('a[href], button:not([disabled])')
    ).filter((el) => el.offsetParent !== null);
  }

  onKeyDown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const items = this.getResultItems();
    if (items.length === 0) return;

    event.preventDefault();
    items[0].focus();
    this.attachItemNav();
  }

  attachItemNav() {
    if (this._navAttached) return;
    this._navAttached = true;

    const handler = (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const items = this.getResultItems();
      const currentIndex = items.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      event.preventDefault();
      if (event.key === 'ArrowDown') {
        const next = items[currentIndex + 1] || items[0];
        next.focus();
      } else if (event.key === 'ArrowUp') {
        if (currentIndex === 0) {
          this.input.focus();
        } else {
          items[currentIndex - 1].focus();
        }
      }
    };

    this.predictiveSearchResults.addEventListener('keydown', handler);
    this._navHandler = handler;
  }

  detachItemNav() {
    if (this._navAttached && this._navHandler && this.predictiveSearchResults) {
      this.predictiveSearchResults.removeEventListener('keydown', this._navHandler);
      this._navAttached = false;
      this._navHandler = null;
    }
  }

  // -- ARIA + announcements ----------------------------------------------

  setExpanded(expanded) {
    if (this.input) this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  announce(message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    window.setTimeout(() => {
      this.liveRegion.textContent = message;
    }, 50);
  }

  onChange() {
    const searchTerm = encodeURIComponent(this.input.value.trim());
    const hint = document.querySelector('.search__hint');

    if (!searchTerm.length) {
      if (this.searchType === 'tier_1') {
        if (hint) hint.classList.remove('show');
        return;
      }

      const initial = this.querySelector('.search__initial');
      if (initial) {
        fadeOut(this.predictiveSearchResults, () => fadeIn(initial));
      } else {
        fadeOut(this.predictiveSearchResults);
      }
      this.setExpanded(false);
      this.detachItemNav();
      return;
    }

    if (this.searchType === 'tier_1') {
      if (hint) hint.classList.add('show');
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    const url =
      `/search?q=${searchTerm}` +
      `&type=product,article,page,collection` +
      `&options[unavailable_products]=show` +
      `&options[prefix]=last` +
      `&view=predictive`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const wrapper = document.querySelector('.navbar__search');
        const isEmpty = text.indexOf('empty-result') !== -1 || text.indexOf('no-results') !== -1;

        if (wrapper) wrapper.classList.toggle('search-results--no-results', isEmpty);

        this.predictiveSearchResults.innerHTML = text;
        if (window.updateProductCardsWishlist) window.updateProductCardsWishlist();

        const itemCount = this.getResultItems().length;
        if (isEmpty || itemCount === 0) {
          this.announce('No results found.');
        } else {
          this.announce(itemCount + ' results available. Use the arrow keys to walk through them.');
        }

        const initial = this.querySelector('.search__initial');
        if (initial && isVisible(initial)) {
          this.predictiveSearchResults.hidden = true;
          fadeOut(initial, () => this.open());
        } else {
          this.open();
        }
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  open() {
    fadeIn(this.predictiveSearchResults);
    this.setExpanded(true);
  }

  close() {
    fadeOut(this.predictiveSearchResults);
    this.setExpanded(false);
    this.detachItemNav();
  }
}

customElements.define('predictive-search', PredictiveSearch);
